import { Platform } from 'react-native';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';

type LoadBundle = (bundlePath: string) => Promise<void>;

const isWeb = Platform.OS === Constants.platformWeb;

// A network error leaves the chunk in place, so only a 404 means a newer deploy removed it.
async function isRemovedChunk(chunkUrl: string): Promise<boolean> {
	try {
		const response = await fetch(chunkUrl, { method: 'HEAD', cache: 'no-store' });

		return response.status === Constants.httpStatusNotFound;
	} catch {
		return false;
	}
}

// Storage the browser refuses counts as a reload already done, so it can never cause a reload loop.
function hasReloadedFor(chunkUrl: string): boolean {
	try {
		return sessionStorage.getItem(Constants.storageChunkReloaded) === chunkUrl;
	} catch {
		return true;
	}
}

function rememberReloadFor(chunkUrl: string): boolean {
	try {
		sessionStorage.setItem(Constants.storageChunkReloaded, chunkUrl);

		return true;
	} catch {
		return false;
	}
}

async function reloadForRemovedChunk(error: unknown): Promise<never> {
	if (
		!WebBootHelper.isChunkLoadError(error) ||
		hasReloadedFor(error.request) ||
		!(await isRemovedChunk(error.request)) ||
		!rememberReloadFor(error.request)
	) {
		throw error;
	}

	window.location.reload();

	return new Promise<never>(() => {});
}

class WebBootHelper {
	// Metro rejects a chunk that fails to load with the URL it requested.
	static isChunkLoadError(error: unknown): error is Error & { request: string } {
		return error instanceof Error && 'request' in error && typeof error.request === 'string';
	}

	private static isSettlingSplash = false;
	private static isFadingSplash = false;
	private static settleFallback: ReturnType<typeof setTimeout> | undefined;

	// Given the height of the login image region, the splash settles onto it first, so the fade shows no shift.
	static hideSplash(imageRegionHeight?: number): void {
		const splash = isWeb ? document.getElementById(Constants.webSplashId) : null;

		if (!splash) {
			return;
		}

		const region = splash.querySelector<HTMLElement>(`.${Constants.webSplashRegionClass}`);
		const settlesOnto =
			region !== null &&
			imageRegionHeight !== undefined &&
			Math.abs(region.offsetHeight - imageRegionHeight) >= Dimens.size1
				? region
				: null;

		if (region && imageRegionHeight !== undefined) {
			region.style.bottom = `${splash.clientHeight - imageRegionHeight}px`;
		}

		if (WebBootHelper.isFadingSplash) {
			return;
		}

		if (!settlesOnto) {
			if (!WebBootHelper.isSettlingSplash) {
				WebBootHelper.fadeOutSplash(splash);
			}

			return;
		}

		if (!WebBootHelper.isSettlingSplash) {
			WebBootHelper.isSettlingSplash = true;
			settlesOnto.addEventListener(Constants.domEventTransitionEnd, (event) => {
				if (event.target === settlesOnto && event.propertyName === Constants.cssPropertyBottom) {
					WebBootHelper.fadeOutSplash(splash);
				}
			});
		}

		// A transition that is cancelled or never runs sends no event, and the splash would stay up for good.
		clearTimeout(WebBootHelper.settleFallback);
		WebBootHelper.settleFallback = setTimeout(() => WebBootHelper.fadeOutSplash(splash), Dimens.animationDuration500);
	}

	private static fadeOutSplash(splash: HTMLElement): void {
		if (WebBootHelper.isFadingSplash) {
			return;
		}

		WebBootHelper.isFadingSplash = true;
		clearTimeout(WebBootHelper.settleFallback);

		const removeFallback = setTimeout(() => splash.remove(), Dimens.animationDuration500);

		splash.addEventListener(Constants.domEventTransitionEnd, (event) => {
			if (event.target === splash && event.propertyName === Constants.cssPropertyOpacity) {
				clearTimeout(removeFallback);
				splash.remove();
			}
		});
		splash.classList.add(Constants.webSplashHiddenClass);
	}

	// Metro loads every chunk through this global, so each dynamic import survives a deploy that removed its chunk.
	static reloadOnRemovedChunks(): void {
		const metroGlobal = globalThis as unknown as { __METRO_GLOBAL_PREFIX__?: string } & Record<string, unknown>;
		const loadBundleKey = `${metroGlobal.__METRO_GLOBAL_PREFIX__ ?? ''}__loadBundleAsync`;
		const loadBundle = metroGlobal[loadBundleKey] as LoadBundle | undefined;

		if (!isWeb || !loadBundle) {
			return;
		}

		metroGlobal[loadBundleKey] = (bundlePath: string) => loadBundle(bundlePath).catch(reloadForRemovedChunk);
	}
}

export default WebBootHelper;
